import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { safeInsert } from 'prosemirror-utils';
import { Fragment, Slice } from 'prosemirror-model';
import { TextSelection } from 'prosemirror-state';
import { mapChildren, flatmap, isEmptyDocument, getStepRange } from '../../utils/slice';
let pluginKey = null;
const LAYOUT_TYPE = {
  TWO_COLS_EQUAL: 'twoColumnsEqual',
  THREE_COLS_EQUAL: 'threeColumnsEqual',
  LEFT_SIDEBAR: 'twoColumnsLeftSidebar',
  RIGHT_SIDEBAR: 'twoColumnsRightSidebar',
  THREE_WITH_SIDEBARS: 'threeColumnsWithSidebars '
};
export const TWO_COL_LAYOUTS = ['two_equal', 'two_left_sidebar', 'two_right_sidebar'];
export const THREE_COL_LAYOUTS = ['three_equal', 'three_with_sidebars'];
export const setPluginKey = key => {
  pluginKey = key;
};
const getWidthsForPreset = presetLayout => {
  switch (presetLayout) {
    case 'three_equal':
      return [33.33, 33.33, 33.33];
    case 'two_left_sidebar':
      return [33.33, 66.66];
    case 'two_right_sidebar':
      return [66.66, 33.33];
    case 'three_with_sidebars':
      return [25, 50, 25];
    default:
      return [50, 50];
  }
};

/**
 * Finds layout preset based on the width attrs of all the layoutColumn nodes
 * inside the layoutSection node
 */
export const getPresetLayout = section => {
  const widths = mapChildren(section, column => column.attrs.width).join(',');
  switch (widths) {
    case '33.33,33.33,33.33':
      return 'three_equal';
    case '25,50,25':
      return 'three_with_sidebars';
    case '50,50':
      return 'two_equal';
    case '33.33,66.66':
      return 'two_left_sidebar';
    case '66.66,33.33':
      return 'two_right_sidebar';
    default:
      return '';
  }
};
export const getSelectedLayout = (maybeLayoutSection, current) => {
  if (maybeLayoutSection && getPresetLayout(maybeLayoutSection)) {
    return getPresetLayout(maybeLayoutSection) || current;
  }
  return current;
};
export const createDefaultLayoutSection = state => {
  const {
    layoutSection,
    layoutColumn
  } = state.schema.nodes;

  // create a 50-50 layout by default
  const columns = Fragment.fromArray([layoutColumn.createAndFill({
    width: 50
  }), layoutColumn.createAndFill({
    width: 50
  })]);
  return layoutSection.createAndFill(undefined, columns);
};
export const insertLayoutColumns = (state, dispatch) => {
  if (dispatch) {
    dispatch(safeInsert(createDefaultLayoutSection(state))(state.tr));
  }
  return true;
};

/**
 * Handles switching from 2 -> 3 cols, or 3 -> 2 cols
 * Switching from 2 -> 3 just adds a new one at the end
 * Switching from 3 -> 2 moves all the content of the third col inside the second before
 * removing it
 */
function forceColumnStructure(state, node, pos, presetLayout) {
  const {
    tr
  } = state;
  const insideRightEdgeOfLayoutSection = pos + node.nodeSize - 1;
  const numCols = node.childCount;
  if (TWO_COL_LAYOUTS.indexOf(presetLayout) >= 0 && numCols === 3) {
    const thirdColumn = node.content.child(2);
    const thirdColumnPos = insideRightEdgeOfLayoutSection - thirdColumn.nodeSize;
    if (isEmptyDocument(thirdColumn)) {
      tr.replaceRange(
      // end pos of second column
      tr.mapping.map(thirdColumnPos - 1), tr.mapping.map(insideRightEdgeOfLayoutSection), Slice.empty);
    } else {
      tr.replaceRange(
      // end pos of second column
      tr.mapping.map(thirdColumnPos - 1),
      // start pos of third column
      tr.mapping.map(thirdColumnPos + 1), Slice.empty);
    }
  } else if (THREE_COL_LAYOUTS.indexOf(presetLayout) >= 0 && numCols === 2) {
    tr.replaceWith(tr.mapping.map(insideRightEdgeOfLayoutSection), tr.mapping.map(insideRightEdgeOfLayoutSection), state.schema.nodes.layoutColumn.createAndFill());
  }
  return tr;
}
function columnWidth(node, schema, widths) {
  const {
    layoutColumn
  } = schema.nodes;
  const truncatedWidths = widths.map(w => Number(w.toFixed(2)));
  return flatmap(node.content, (column, idx) => layoutColumn.create(_objectSpread(_objectSpread({}, column.attrs), {}, {
    width: truncatedWidths[idx]
  }), column.content, column.marks));
}
function forceColumnWidths(state, tr, pos, presetLayout) {
  const node = tr.doc.nodeAt(pos);
  if (!node) {
    return tr;
  }
  return tr.replaceWith(pos + 1, pos + node.nodeSize - 1, columnWidth(node, state.schema, getWidthsForPreset(presetLayout)));
}
export function forceSectionToPresetLayout(state, node, pos, presetLayout) {
  let tr = forceColumnStructure(state, node, pos, presetLayout);

  // save the selection here, since forcing column widths causes a change over the
  // entire layoutSection, which remaps selection to the end. not remapping here
  // is safe because the structure is no longer changing.
  const {
    selection
  } = tr;
  tr = forceColumnWidths(state, tr, pos, presetLayout);
  return tr.setSelection(new TextSelection(tr.doc.resolve(selection.$from.pos)));
}
export const setPresetLayout = layout => (state, dispatch) => {
  const {
    pos,
    selectedLayout
  } = pluginKey.getState(state);
  if (selectedLayout === layout || pos === null) {
    return false;
  }
  const node = state.doc.nodeAt(pos);
  if (!node) {
    return false;
  }
  const tr = forceSectionToPresetLayout(state, node, pos, layout);
  if (tr) {
    tr.setMeta('scrollIntoView', false);
    if (dispatch) {
      dispatch(tr);
    }
    return true;
  }
  return false;
};
export const fixColumnSizes = (changedTr, state) => {
  const {
    layoutSection
  } = state.schema.nodes;
  let change;
  const range = getStepRange(changedTr);
  if (!range) {
    return undefined;
  }
  changedTr.doc.nodesBetween(range.from, range.to, (node, pos) => {
    if (node.type === layoutSection) {
      if (getPresetLayout(node)) {
        return false;
      }
      const presetLayout = node.childCount === 2 ? 'two_equal' : 'three_equal';
      const fixedColumns = columnWidth(node, state.schema, getWidthsForPreset(presetLayout));
      change = {
        from: pos + 1,
        to: pos + node.nodeSize - 1,
        slice: new Slice(fixedColumns, 0, 0)
      };
      return false;
    }
    return true;
  });
  return change;
};
export const fixColumnStructure = state => {
  const {
    pos,
    selectedLayout
  } = pluginKey.getState(state);
  if (pos !== null && selectedLayout) {
    const node = state.doc.nodeAt(pos);
    if (node && node.childCount !== getWidthsForPreset(selectedLayout).length) {
      return forceSectionToPresetLayout(state, node, pos, selectedLayout);
    }
  }
  return null;
};
export const deleteActiveLayoutNode = (state, dispatch) => {
  const {
    pos
  } = pluginKey.getState(state);
  if (pos !== null) {
    const node = state.doc.nodeAt(pos);
    if (dispatch) {
      const tr = state.tr.delete(pos, pos + node.nodeSize);
      dispatch(tr);
    }
    return true;
  }
  return false;
};
export const formatLayoutName = layout => {
  switch (layout) {
    case 'three_equal':
      return LAYOUT_TYPE.THREE_COLS_EQUAL;
    case 'two_left_sidebar':
      return LAYOUT_TYPE.LEFT_SIDEBAR;
    case 'two_right_sidebar':
      return LAYOUT_TYPE.RIGHT_SIDEBAR;
    case 'three_with_sidebars':
      return LAYOUT_TYPE.THREE_WITH_SIDEBARS;
    default:
      return LAYOUT_TYPE.TWO_COLS_EQUAL;
  }
};